import type {
  PhoneCall,
  PhoneCallDisposition,
  PhoneCallHoldState,
  PhoneCallOutcome as PhoneCallOutcomeWire,
  PhoneCallStatus,
} from "@phone/phoneCallsClient";
import type { SwipeTone } from "@scribenote/brian-design-system";

export type PhoneCallTone = SwipeTone | "neutral";

interface PhoneCallOutcome {
  label: string;
  tone: PhoneCallTone;
  helper?: string;
}

const INBOUND_OUTCOMES: Partial<
  Record<PhoneCallDisposition, PhoneCallOutcome>
> = {
  message_taken: { label: "Message taken", tone: "brian" },
  handed_off: { label: "Connected to clinic", tone: "brian" },
  handoff_failed: { label: "Handoff failed", tone: "stopped" },
  caller_abandoned: { label: "Caller hung up", tone: "flag" },
};

const NO_ANSWER: PhoneCallOutcome = {
  label: "No answer",
  tone: "neutral",
  helper: "Try again later.",
};
const NOT_STARTED: PhoneCallOutcome = {
  label: "Not started",
  tone: "flag",
  helper: "The call never left the clinic.",
};
const CALL_FAILED: PhoneCallOutcome = {
  label: "Call failed",
  tone: "stopped",
  helper: "Brian couldn't complete it; try again, tell us if it repeats.",
};

/** Groups brian's `PhoneCallDisposition` (models/phone_call.py): recipient's side, clinic's side, Brian's or the carrier's. */
const OUTBOUND_OUTCOMES: Partial<
  Record<PhoneCallDisposition, PhoneCallOutcome>
> = {
  recipient_no_answer: NO_ANSWER,
  recipient_busy: NO_ANSWER,
  recipient_ended_before_bridge: NO_ANSWER,
  cancelled_by_user: NOT_STARTED,
  staff_hangup_before_connection: NOT_STARTED,
  staff_no_answer: NOT_STARTED,
  staff_line_busy: NOT_STARTED,
  staff_confirmation_timeout: NOT_STARTED,
  dispatch_not_started: NOT_STARTED,
  recipient_failed: CALL_FAILED,
  bridge_failed: CALL_FAILED,
  capture_start_failed: CALL_FAILED,
  staff_confirmation_unavailable: CALL_FAILED,
};

const OUTCOME_HELPERS: Partial<Record<PhoneCallOutcomeWire, string>> = {
  spoke_with_recipient: "Spoke with them",
  left_voicemail: "Left a voicemail",
};

const unknownDisposition = (disposition: string): PhoneCallOutcome => ({
  label: disposition.replace(/_/g, " "),
  tone: "neutral",
});

/**
 * `disposition` wins whenever brian has settled on one; `status` only answers
 * while it hasn't. No outcome claims a human spoke: `bridged` may be voicemail.
 */
export const phoneCallOutcome = (
  status: PhoneCallStatus,
  disposition: PhoneCallDisposition | null,
  holdState: PhoneCallHoldState = "none",
): PhoneCallOutcome => {
  if (disposition) {
    return INBOUND_OUTCOMES[disposition] ?? unknownDisposition(disposition);
  }
  if (status !== "ended") {
    return holdState === "none"
      ? { label: "In progress", tone: "working" }
      : { label: "On hold", tone: "flag" };
  }
  return { label: "Ended", tone: "neutral" };
};

/** brian only writes an outbound disposition when something cut the call short. */
export const outboundCallOutcome = (
  status: PhoneCallStatus,
  disposition: PhoneCallDisposition | null,
  outcome: PhoneCallOutcomeWire | null,
): PhoneCallOutcome => {
  if (disposition) {
    return OUTBOUND_OUTCOMES[disposition] ?? unknownDisposition(disposition);
  }
  if (status !== "ended") return { label: "In progress", tone: "working" };
  return {
    label: "Completed",
    tone: "brian",
    helper: outcome ? OUTCOME_HELPERS[outcome] : undefined,
  };
};

/** Ended per the frame, row unread: a null disposition means "Completed" only off the row. */
export const OUTBOUND_ENDED_UNREAD: PhoneCallOutcome = {
  label: "Call ended",
  tone: "neutral",
};

export const callOutcome = (
  call: Pick<
    PhoneCall,
    "origin" | "status" | "disposition" | "outcome" | "holdState"
  >,
): PhoneCallOutcome =>
  call.origin === "staff_outbound"
    ? outboundCallOutcome(call.status, call.disposition, call.outcome)
    : phoneCallOutcome(call.status, call.disposition, call.holdState);

const pad = (seconds: number): string => String(seconds).padStart(2, "0");

export const formatSeconds = (total: number): string => {
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = pad(total % 60);
  return hours > 0
    ? `${hours}:${pad(minutes)}:${seconds}`
    : `${minutes}:${seconds}`;
};

/** Null for a range that runs backwards: a clock skew is not a duration. */
export const formatDuration = (
  startedAt: string | null,
  endedAt: string | null,
): string | null => {
  if (!startedAt || !endedAt) return null;
  const ms = new Date(endedAt).getTime() - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  return formatSeconds(Math.round(ms / 1000));
};

/** brian's `finalize_derivation` lands within minutes; older calls were never summarized. */
const SUMMARY_PENDING_WINDOW_MS = 10 * 60 * 1000;

export const isSummaryPending = (
  call: Pick<PhoneCall, "status" | "summary" | "endedAt">,
  now: number,
): boolean => {
  if (call.status !== "ended" || call.summary !== null || !call.endedAt) {
    return false;
  }
  const endedAt = new Date(call.endedAt).getTime();
  if (!Number.isFinite(endedAt)) return false;
  const age = now - endedAt;
  return age >= 0 && age < SUMMARY_PENDING_WINDOW_MS;
};
